import type { InputT } from "@/types/database/forms";

import type { UpsertEntryValueT } from "../../inputs/hooks/useEntryValuesStore";

interface TriedTreatmentValue {
	hasTried: boolean;
	duration: string;
	isOngoing?: string;
}

interface FormValue {
	[key: string]:
		| string
		| number
		| boolean
		| Date
		| string[]
		| { timeUnit: string; interval: number }
		| TriedTreatmentValue;
}

export const formatEntryValues = (
	formValues: Record<string, FormValue>,
	inputs: InputT[],
	entryId: string,
	repeatableGroupInputs?: InputT[],
): UpsertEntryValueT[] => {
	// Filter out any form values that don't have a corresponding input
	const validInputs = inputs.filter((input) => input.input_id);

	// Log any missing inputs for debugging
	const missingInputs = Object.keys(formValues).filter(
		(id) => !validInputs.some((input) => input.input_id === id),
	);
	if (missingInputs.length > 0) {
		console.warn("Missing inputs:", missingInputs);
	}

	// Only process form values that have corresponding valid inputs
	return Object.entries(formValues)
		.filter(([id]) => {
			const inputExists = validInputs.some((input) => input.input_id === id);
			if (!inputExists) {
				console.warn(`Skipping form value for non-existent input_id: ${id}`);
			}
			return inputExists;
		})
		.map(([id, value]) => {
			const input = inputs.find((input) => input.input_id === id);
			if (!input?.input_key) {
				console.warn(`Input not found or missing input_key for id: ${id}`);
				return null;
			}

			// Create base entry value object with all fields optional
			const baseEntryValue: Partial<UpsertEntryValueT> = {
				input_id: id,
				entry_id: entryId,
				created_at: new Date().toISOString(),
				last_edited: new Date().toISOString(),
				number_value: null,
				object_id: null,
				text_array: null,
				text_value: null,
				json_value: null,
			};

			if (
				[
					"scale-rating",
					"number",
					"slider",
					"pain-slider",
					"glucose-slider",
					"visa-a-8",
					"visa-g-8",
					"visa-h-8",
					"visa-p-8",
				].includes(input.input_key)
			) {
				return {
					...baseEntryValue,
					number_value: value === null ? null : Number(value),
				} as UpsertEntryValueT;
			}
			if (["multiple-choice"].includes(input.input_key)) {
				return {
					...baseEntryValue,
					text_array: Array.isArray(value) ? [...value] : [value],
				} as UpsertEntryValueT;
			}
			if (["duration"].includes(input.input_key)) {
				return {
					...baseEntryValue,
					time_unit: (value as { timeUnit: string }).timeUnit,
					number_value: Number((value as { interval: number }).interval),
				} as UpsertEntryValueT;
			}
			if (
				["date-picker", "date-time-picker", "date"].includes(input.input_key)
			) {
				return {
					...baseEntryValue,
					date_value:
						value instanceof Date
							? value.toISOString()
							: typeof value === "string"
								? new Date(value).toISOString()
								: new Date().toISOString(),
				} as UpsertEntryValueT;
			}
			if (["repeatable-group"].includes(input.input_key)) {
				const groupValues = Array.isArray(value) ? value : [];
				const formattedGroups = formatRepeatableGroupsValue(
					groupValues,
					repeatableGroupInputs ?? [],
				);
				return {
					...baseEntryValue,
					json_value: { groups: formattedGroups } as Record<string, unknown>,
				} as UpsertEntryValueT;
			}
			if (["tried-treatment"].includes(input.input_key)) {
				// Cast the value to unknown first to avoid TypeScript errors
				const triedValue = value as unknown as TriedTreatmentValue;
				if (!triedValue?.hasTried || !triedValue?.duration) {
					console.warn(
						`Invalid tried-treatment value for input ${id}:`,
						triedValue,
					);
					return null;
				}
				return {
					...baseEntryValue,
					json_value: triedValue,
				} as UpsertEntryValueT;
			}
			return {
				...baseEntryValue,
				text_value: String(value),
			} as UpsertEntryValueT;
		})
		.filter((value): value is UpsertEntryValueT => value !== null);
};
import type { RepeatableGroup } from "./inputSettings";

export const formatRepeatableGroupsValue = (
	value: RepeatableGroup,
	repeatableGroupInputs: InputT[],
) => {
	return value.map((group) => {
		const processedValues = group.values.flatMap((item) => {
			const entries = Object.entries(item);
			return entries.map(([key, val]) => {
				const foundInput = repeatableGroupInputs?.find(
					(input) => input.input_id === key,
				);
				return {
					input_id: key,
					value: val,
					input_key: foundInput?.input_key || "",
					input_label: foundInput?.input_label || "",
				};
			});
		});
		console.log("processedValues repeated group", processedValues);
		return processedValues;
	});
};
